import {
    useMemo, useRef, useState, HTMLAttributes, memo, RefObject, PointerEventHandler, Dispatch, SetStateAction,
    MouseEventHandler, useContext,
} from 'react'
import { useMediaQuery } from 'react-responsive'
import { arc } from 'd3'
import cn from 'classnames'
import { ElementSize, useResizeObserver } from '~/hooks/client/useResizeObserver'

import { ChartContext, defaultChartContext } from '~/components/data-display/charts/context'

interface ArcMarketCapProps extends HTMLAttributes<HTMLDivElement>
{
    total: number
    privacy: number
    fontFamily?: string
}

interface ArcsParams
{
    transformX: number
    transformY: number
}

interface LineParams
{
    x1: number
    x2: number
    y1: number
    y2: number
}

interface LineTextParams
{
    x: number
    y: number
    textAnchor: 'start' | 'end'
}

type ActiveCap = 'global' | 'private'

const fmt = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
    notation: 'compact',
    compactDisplay: 'long',
})

export function ArcMarketCapFallback({ total, privacy, fontFamily, className }: ArcMarketCapProps) {
    return (
        <table className={cn('table', className)}>
            <thead>
            <tr>
                <th className={cn('text-end')}>Total Coin Market Cap</th>
                <th className={cn('text-end')}>Privacy Market Cap</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td className={cn('text-end')}>{fmt.format(total)}</td>
                <td className={cn('text-end')}>{fmt.format(privacy)}</td>
            </tr>
            </tbody>
        </table>
    )
}

export function ArcMarketCap ({
    total, // 3_850_000_000_000
    privacy, // 14_880_000_000
    fontFamily = 'monospace',
    className = '',
}: ArcMarketCapProps)
{
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
    // const isSmallDesktop = useMediaQuery({ query: '(min-width: 1280px) and (max-width: 1535px)' })
    const isSmallDesktop = false

    const [activeCap, setActiveCap] = useState<ActiveCap>('private')

    const rootRef = useRef<HTMLDivElement>(null)
    const svgSize = useResizeObserver(rootRef)

    const privacyCapTextRef = useRef<SVGTextElement>(null)
    const privacyCapTextSize = useResizeObserver(privacyCapTextRef)

    const globalCapTextRef = useRef<SVGTextElement>(null)
    const globalCapTextSize = useResizeObserver(globalCapTextRef)

    const valuesRef = useRef<SVGTextElement>(null)
    const valuesSize = useResizeObserver(valuesRef)

    const arcsParams = useMemo<ArcsParams>(() => (
        {
            transformX: svgSize.width / 2,
            transformY: svgSize.height - 1,
        }
    ), [svgSize])

    const percentage = useMemo(() =>
    {
        if (total === 0) {
            return 0
        }

        return Math.max(Math.round(privacy / total * 100 * 100) / 100 / 100, 0.04)

        // return Math.round(Math.log10(privacy) / Math.log2(total) * 100 * 100) / 100 / 100
    }, [total, privacy])

    const radius = useMemo(() =>
    {
        let radius = svgSize.width / 2
        if (radius > svgSize.height) {
            radius = svgSize.height
        }

        let d = radius * 2
        if (!isMobile && !isSmallDesktop) {
            if (radius * 2 + globalCapTextSize.width * 2 + 2 > svgSize.width) {
                d = svgSize.width - globalCapTextSize.width * 2 - 8
            }

            if (radius * 2 + privacyCapTextSize.width * 2 + 2 > svgSize.width) {
                d = svgSize.width - privacyCapTextSize.width * 2 - 8
            }

            if (radius * 2 + privacyCapTextSize.width + globalCapTextSize.width + 2 > svgSize.width) {
                d = svgSize.width - privacyCapTextSize.width - globalCapTextSize.width - 8
            }
        }

        return Math.max(1, d / 2 - 2)
    }, [isMobile, isSmallDesktop, svgSize, privacyCapTextSize.width, globalCapTextSize.width])

    return (
        <ChartContext
            value={{
                ...defaultChartContext,
                fontFamily,
            }}
        >
            <div className={cn('relative', className)} ref={rootRef}>
                <svg viewBox={`0 0 ${svgSize.width} ${svgSize.height}`} className={'w-full h-full'}>
                    <Background radius={radius} svgSize={svgSize} />

                    <Annotations
                        svgSize={svgSize}
                        radius={radius}
                        percentage={percentage}
                        privacyCapTextSize={privacyCapTextSize}
                        globalCapTextSize={globalCapTextSize}
                        privacyCapTextRef={privacyCapTextRef}
                        globalCapTextRef={globalCapTextRef}
                        activeCap={activeCap}
                        setActiveCap={setActiveCap}
                    />

                    <Arcs
                        radius={radius}
                        percentage={percentage}
                        svgSize={svgSize}
                        arcsParams={arcsParams}
                        activeCap={activeCap}
                    />

                    <Values
                        valTotal={total}
                        valPrivacy={privacy}
                        svgSize={svgSize}
                        valuesSize={valuesSize}
                        ref={valuesRef}
                        activeCap={activeCap}
                        setActiveCap={setActiveCap}
                    />
                </svg>
            </div>
        </ChartContext>
    )
}

const Background = memo(({
    radius,
    svgSize,
}: {
    radius: number,
    svgSize: ElementSize
}) => (
    <>
        <defs>
            <radialGradient id="arcMarketCap_gradient" r={1}>
                <stop offset="0%" stopOpacity={0.6} style={{ stopColor: 'var(--color-accent)' }} />
                <stop offset="45%" stopOpacity={0} style={{ stopColor: 'var(--color-accent)' }} />
            </radialGradient>

            <filter
                id="arcMarketCap_filter_noise"
                filterUnits="objectBoundingBox"
                primitiveUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
            >
                <feTurbulence
                    type="fractalNoise"
                    baseFrequency="0.104"
                    numOctaves="4"
                    seed="15"
                    stitchTiles="stitch"
                    x="0%"
                    y="0%"
                    width="100%"
                    height="100%"
                    result="turbulence"
                ></feTurbulence>
                <feSpecularLighting
                    surfaceScale="25"
                    specularConstant="5"
                    specularExponent="20"
                    x="0%"
                    y="0%"
                    width="100%"
                    height="100%"
                    in="turbulence"
                    result="specularLighting"
                    style={{ lightingColor: 'var(--color-accent)' }}
                >
                    <feDistantLight azimuth="3" elevation="22"></feDistantLight>
                </feSpecularLighting>

                <feComposite operator="in" in2="SourceGraphic" />

            </filter>
        </defs>

        {/* bg circle */}
        <circle
            cx={svgSize.width / 2}
            cy={svgSize.height - 1}
            r={radius}
            fill={`url(#arcMarketCap_gradient)`}
            opacity={0.75}
        />
        <circle
            cx={svgSize.width / 2}
            cy={svgSize.height - 1}
            r={radius}
            fill={`url(#arcMarketCap_gradient)`}
            filter={`url(#arcMarketCap_filter_noise)`}
            style={{ mixBlendMode: 'revert-layer' }}
            opacity={/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? 0.5 : 1}
        />
    </>
))

const Annotations = memo(({
    svgSize,
    radius,
    percentage,
    privacyCapTextSize,
    globalCapTextSize,
    privacyCapTextRef,
    globalCapTextRef,
    activeCap,
    setActiveCap,
}: {
    svgSize: ElementSize,
    radius: number,
    percentage: number,
    privacyCapTextSize: ElementSize,
    globalCapTextSize: ElementSize,
    privacyCapTextRef: RefObject<SVGTextElement | null>,
    globalCapTextRef: RefObject<SVGTextElement | null>,
    activeCap: ActiveCap,
    setActiveCap: Dispatch<SetStateAction<ActiveCap>>,
}) =>
{
    const { fontFamily } = useContext(ChartContext)

    const capTextParams = {
        dy: 6,
        dx: 6,
    }

    const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
    // const isSmallDesktop = useMediaQuery({ query: '(min-width: 1280px) and (max-width: 1535px)' })
    const isSmallDesktop = false

    const {
        privacyCapLine,
        globalCapLine,
    }: {
        privacyCapLine: LineParams,
        globalCapLine: LineParams
    } = useMemo(() =>
    {
        if (isMobile || isSmallDesktop) {
            return {
                privacyCapLine: {
                    x1: svgSize.width / 2 - radius - 1,
                    x2: svgSize.width / 2 - radius - 1,
                    y1: 0,
                    y2: svgSize.height,
                },
                globalCapLine: {
                    x1: svgSize.width / 2 + radius + 1,
                    x2: svgSize.width / 2 + radius + 1,
                    y1: 0,
                    y2: svgSize.height,
                },
            }
        }

        const xCenter = svgSize.width / 2
        const yCenter = svgSize.height

        const theta = -Math.PI / 2 + Math.PI * (
            percentage - 0.5
        )
        const x = (
            xCenter + radius * Math.cos(theta)
        )
        const y = (
            yCenter + radius * Math.sin(theta)
        )

        const percentageRight = 0.91
        const thetaRight = -Math.PI / 2 + Math.PI * (
            percentageRight - 0.5
        )
        const xRight = xCenter + radius * Math.cos(thetaRight)
        const yRight = yCenter + radius * Math.sin(thetaRight)

        return {
            privacyCapLine: {
                x1: percentage > 0.5
                    ? svgSize.width
                    : 0,
                x2: x - 2,
                y1: y,
                y2: y,
            },
            globalCapLine: {
                x1: xRight + 2,
                x2: svgSize.width,
                y1: yRight,
                y2: yRight,
            },
        }
    }, [isMobile, isSmallDesktop, svgSize, radius, percentage])

    const privacyCapTextParams: LineTextParams = useMemo(() =>
    {
        if (isMobile || isSmallDesktop) {
            return {
                x: privacyCapLine.x1 + capTextParams.dx,
                y: privacyCapTextSize.height / 3,
                textAnchor: 'start',
            }
        }

        let y = privacyCapLine.y1 - capTextParams.dy - privacyCapTextSize.height / 2
        if (y < 0) {
            y += capTextParams.dy * 2 + privacyCapTextSize.height
        }

        return {
            x: privacyCapLine.x1,
            y,
            textAnchor: (
                privacyCapLine.x1 < svgSize.width / 2
            )
                ? 'start'
                : 'end',
        }
    }, [isMobile, isSmallDesktop, privacyCapLine, privacyCapTextSize.height, svgSize.width])

    const globalCapTextParams: LineTextParams = useMemo(() =>
    {
        if (isMobile || isSmallDesktop) {
            return {
                x: globalCapLine.x1 - capTextParams.dx,
                y: privacyCapTextSize.height / 3,
                textAnchor: 'end',
            }
        }

        let y = globalCapLine.y1 - capTextParams.dy - globalCapTextSize.height / 2

        return {
            x: globalCapLine.x2,
            y,
            textAnchor: 'end',
        }
    }, [isMobile, isSmallDesktop, globalCapLine, globalCapTextSize.height, svgSize.width])

    const globalDown: PointerEventHandler = () => setActiveCap('global')
    const privateDown: PointerEventHandler = () => setActiveCap('private')
    const globalMouseEnter: MouseEventHandler = () => setActiveCap('global')
    const globalMouseLeave: MouseEventHandler = () => setActiveCap('private')

    return (
        <>
            <line
                strokeDasharray={'1'}
                strokeOpacity={0.4}
                x1={privacyCapLine.x1}
                x2={privacyCapLine.x2}
                y1={privacyCapLine.y1}
                y2={privacyCapLine.y2}
                className={'stroke-accent'}
            />

            <line
                strokeDasharray={'1'}
                strokeOpacity={0.4}
                x1={globalCapLine.x1}
                x2={globalCapLine.x2}
                y1={globalCapLine.y1}
                y2={globalCapLine.y2}
                className={'stroke-accent'}
            />

            <text
                ref={privacyCapTextRef}
                textAnchor={privacyCapTextParams.textAnchor}
                alignmentBaseline={'middle'}
                fontSize={14}
                fontFamily={fontFamily}
                fontWeight={500}
                letterSpacing={0.4}
                opacity={activeCap === 'private'
                    ? 1
                    : 0.4}
                transform={`translate(${privacyCapTextParams.x} ${privacyCapTextParams.y})`}
                className={'fill-accent'}
                style={{ transition: `opacity 132ms ease` }}
                onPointerDown={privateDown}
            >
                {(
                    isMobile || isSmallDesktop
                )
                    ? (
                        <>
                            <tspan x={0}>PRIVACY</tspan>
                            <tspan x={0} dy={'1em'}>MARKET</tspan>
                            <tspan x={0} dy={'1em'}>CAP</tspan>
                        </>
                    )
                    : (
                        <>
                            <tspan x={0}>PRIVACY</tspan>
                            <tspan x={0} dy={'1em'}>MARKET CAP</tspan>
                        </>
                    )}
            </text>

            <text
                ref={globalCapTextRef}
                textAnchor={globalCapTextParams.textAnchor}
                alignmentBaseline={'middle'}
                fontSize={14}
                fontFamily={fontFamily}
                fontWeight={500}
                letterSpacing={0.4}
                opacity={activeCap === 'global'
                    ? 1
                    : 0.4}
                transform={`translate(${globalCapTextParams.x} ${globalCapTextParams.y})`}
                className={'fill-accent'}
                style={{ transition: `opacity 132ms ease` }}
                onMouseEnter={globalMouseEnter}
                onMouseLeave={globalMouseLeave}
                onPointerDown={globalDown}
            >
                {(
                    isMobile || isSmallDesktop
                )
                    ? (
                        <>
                            <tspan x={0}>TOTAL COIN</tspan>
                            <tspan x={0} dy={'1em'}>MARKET</tspan>
                            <tspan x={0} dy={'1em'}>CAP</tspan>
                        </>
                    )
                    : (
                        <>
                            <tspan x={0}>TOTAL COIN</tspan>
                            <tspan x={0} dy={'1em'}>MARKET CAP</tspan>
                        </>
                    )}
            </text>
        </>
    )
})

const Arcs = memo(({
    radius,
    percentage,
    svgSize,
    arcsParams,
    activeCap,
}: {
    radius: number,
    percentage: number,
    svgSize: ElementSize,
    arcsParams: ArcsParams,
    activeCap: ActiveCap,
}) =>
{
    const {
        fg,
        bg,
    } = useMemo(() =>
    {
        /* desktop (semi-circle) */
        const arcGen = arc()
        const bg = arcGen({
            outerRadius: radius,
            innerRadius: radius,
            startAngle: -Math.PI / 2,
            endAngle: Math.PI / 2,
        }) ?? ''
        const fg = arcGen({
            outerRadius: radius,
            innerRadius: radius,
            startAngle: -Math.PI / 2,
            endAngle: -Math.PI / 2 + percentage * Math.PI,
        }) ?? ''

        return {
            bg,
            fg,
        }
    }, [arcsParams, percentage, svgSize])

    return (
        <>
            <path
                d={bg}
                strokeWidth={1}
                strokeOpacity={activeCap === 'global'
                    ? 0.8
                    : 0.4}
                fill="none"
                transform={`translate(${arcsParams.transformX}, ${arcsParams.transformY})`}
                className={'stroke-accent'}
                style={{ transition: `stroke-opacity 132ms ease` }}
            />

            <path
                d={fg}
                strokeWidth={1}
                strokeOpacity={activeCap === 'private'
                    ? 0.8
                    : 0}
                fill="none"
                transform={`translate(${arcsParams.transformX}, ${arcsParams.transformY})`}
                className={'stroke-accent'}
                style={{ transition: `stroke-opacity 132ms ease` }}
            />
        </>
    )
})

const Values = memo(({
    valTotal,
    valPrivacy,
    svgSize,
    valuesSize,
    ref,
    activeCap,
    setActiveCap,
}: {
    valTotal: number,
    valPrivacy: number,
    svgSize: ElementSize,
    valuesSize: ElementSize,
    ref: RefObject<SVGTextElement | null>,
    activeCap: ActiveCap,
    setActiveCap: Dispatch<SetStateAction<ActiveCap>>,
}) =>
{
    const { fontFamily } = useContext(ChartContext)

    const globalDown: PointerEventHandler = () => setActiveCap('global')
    const privateDown: PointerEventHandler = () => setActiveCap('private')
    const globalMouseEnter: MouseEventHandler = () => setActiveCap('global')
    const globalMouseLeave: MouseEventHandler = () => setActiveCap('private')
    const privateMouseEnter: MouseEventHandler = () => setActiveCap('private')

    const format = (value: number) => `$${fmt.format(value).toUpperCase()}`

    return (
        <text
            ref={ref}
            textAnchor={'middle'}
            alignmentBaseline={'baseline'}
            fontSize={14}
            fontFamily={fontFamily}
            fontWeight={500}
            letterSpacing={0.4}
            transform={`translate(${svgSize.width / 2}, ${svgSize.height - valuesSize.height})`}
            className={'fill-accent'}
        >
            <tspan
                x={0}
                onMouseEnter={globalMouseEnter}
                onMouseLeave={globalMouseLeave}
                onPointerDown={globalDown}
                className={cn(activeCap === 'global'
                    ? 'fill-accent'
                    : 'fill-base-100')}
                style={{ transition: `fill 132ms ease` }}
            >{format(valTotal)}</tspan>
            <tspan
                x={0}
                dy={'1.3em'}
                className={cn(activeCap === 'global'
                    ? 'fill-base-100'
                    : 'fill-accent')}
                style={{ transition: `fill 132ms ease` }}
                onPointerDown={privateDown}
                onMouseEnter={privateMouseEnter}
            >{format(valPrivacy)}</tspan>
        </text>
    )
})